/**
 * Who builds each module of one programme, and how the work is spread.
 *
 * The picker, the workload summary and the table are one section because they
 * answer one question in three steps: who is free, who has what, and what is
 * still unassigned.
 *
 * @module components/teams/ModuleOwnership
 */

import React from "react";

import type { BuilderLoad } from "@/components/teams/team-assignments";
import {
  Badge,
  CollectionView,
  type Column,
  countLabel,
  EmptyState,
  Eyebrow,
  Icon,
  MutedText,
  Panel,
  type RowActionsSpec,
  Text,
} from "@/components/ui";
import type { ModuleOwnershipAssignment } from "@/server/types";

/** Names the chip row it heads, which has no other source for what it is. */
const WORKLOAD_HEADING_ID = "module-builder-workload-heading";

/** One module and whoever is building it, resolved for display. */
export interface ModuleOwnerRow {
  id: string;
  title: string;
  code: string;
  ownerName: string | null;
  ownership: ModuleOwnershipAssignment | null;
}

export interface ModuleOwnershipProps {
  rows: readonly ModuleOwnerRow[];
  workload: readonly BuilderLoad[];
  /** The picker and the assign control, which the container owns. */
  picker: React.ReactNode;
  assignedCount: number;
  onRevoke: (ownership: ModuleOwnershipAssignment) => void;
  revokeDisabled: boolean;
  /** Why removal refuses, announced on the control that refuses it. */
  revokeReason?: string;
}

function ownerCell(row: ModuleOwnerRow): React.ReactElement {
  if (row.ownership) {
    return <>{row.ownerName ?? row.ownership.userId}</>;
  }
  return (
    <Badge tone="warning" variant="soft" size="sm">
      Unassigned
    </Badge>
  );
}

export function ModuleOwnership({
  rows,
  workload,
  picker,
  assignedCount,
  onRevoke,
  revokeDisabled,
  revokeReason = undefined,
}: ModuleOwnershipProps): React.ReactElement {
  const rowActions: RowActionsSpec<ModuleOwnerRow> = {
    label: (row) => row.title,
    testId: (row) => `module-owner-actions-${row.id}`,
    items: (row) => {
      const { ownership } = row;
      if (!ownership) {
        return [];
      }
      const remove = {
        key: "remove",
        label: "Remove builder",
        icon: "user-minus",
        tone: "danger" as const,
        confirm: {
          title: `Remove ${row.ownerName ?? "the builder"} from ${row.title}?`,
          body: "The module is unassigned until someone else is assigned to build it.",
          confirmLabel: "Remove builder",
          "data-testid": "module-owner-remove-confirmation",
        },
        onSelect: () => {
          onRevoke(ownership);
        },
        "data-testid": `module-owner-remove-${row.id}`,
      };
      return [
        revokeDisabled
          ? {
              ...remove,
              disabled: true as const,
              disabledReason: revokeReason ?? "Not available now",
            }
          : remove,
      ];
    },
  };
  const columns: Column<ModuleOwnerRow>[] = [
    {
      key: "module",
      header: "Module",
      render: (row) => (
        <>
          <div className="font-medium">{row.title}</div>
          <MutedText size="caption" as="div">
            {row.code}
          </MutedText>
        </>
      ),
    },
    { key: "builder", header: "Builder", render: ownerCell },
  ];

  return (
    <Panel
      attached
      icon="squares-four"
      title="Module builders"
      className="min-w-0"
      actions={
        <>
          <Badge
            tone="neutral"
            variant="outline"
            size="sm"
            aria-label={`${assignedCount} of ${rows.length} ${
              rows.length === 1 ? "module has" : "modules have"
            } a builder`}
          >
            {assignedCount}/{rows.length}
          </Badge>
          {picker}
        </>
      }
    >
      <div data-testid="module-builder-workload">
        {/* An `h3` so heading navigation finds the group; the chips name themselves by it. */}
        <Eyebrow as="h3" id={WORKLOAD_HEADING_ID} className="flex items-center gap-2">
          <Icon name="chart-bar" aria-hidden />
          Builder workload in this programme
        </Eyebrow>
        <div
          role="group"
          aria-labelledby={WORKLOAD_HEADING_ID}
          className="mt-2 flex flex-wrap gap-2"
        >
          {workload.length ? (
            workload.map((load) => (
              <Badge
                key={load.userId}
                tone={load.inactive ? "warning" : "neutral"}
                variant="outline"
                size="sm"
                className="h-auto gap-1 py-2"
              >
                <span>{load.displayName}</span>
                <span className="font-semibold">{countLabel(load.moduleCount, "module")}</span>
                {/* Named, not dropped: the module table shows this account as
                    the owner, so the work is real and needs moving. */}
                {load.inactive && <span>(deactivated)</span>}
              </Badge>
            ))
          ) : (
            <Text as="span" tone="muted">
              No Module Builders assigned.
            </Text>
          )}
        </div>
      </div>

      <div className="mt-4">
        <CollectionView
          storageKey="module-owners-table"
          caption="Modules and their builders"
          columns={columns}
          rowActions={rowActions}
          rows={rows}
          rowKey={(row) => row.id}
          density="sm"
          emptyState={
            <EmptyState
              icon="squares-four"
              title="No modules have been created for this programme."
              density="row"
            />
          }
          data-testid="module-owners-table"
        />
      </div>
    </Panel>
  );
}
